import { useState, useEffect, useRef } from "react";
import { Trophy } from "lucide-react";

const API_BASE = "/project";

const formatSol = (val) => {
  if (val === null || val === undefined) return "—";
  const num = Number(val);
  if (!Number.isFinite(num)) return "—";
  return `${num.toFixed(4)} SOL`;
};

const useIsVisible = (ref, options = {}) => {
  const [isVisible, setIsVisible] = useState(false);
  
  useEffect(() => {
    if (!ref.current) return;
    
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsVisible(true);
        if (options.once !== false) {
          observer.disconnect();
        }
      }
    }, { threshold: options.amount || 0.3 });
    
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [ref, options.once, options.amount]);
  
  return isVisible;
};

export default function Contest() {
  const sectionRef = useRef(null);
  const isVisible = useIsVisible(sectionRef, { once: true });
  const [leaderboards, setLeaderboards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    fetch(`${API_BASE}/api/contests/leaderboard`)
      .then((res) => res.json())
      .then((data) => {
        if (!active) return;
        if (!data?.success) throw new Error(data?.message || "Failed to load leaderboards");
        setLeaderboards(data.leaderboards || []);
      })
      .catch((err) => {
        if (!active) return;
        setError(err.message || "Failed to load leaderboards");
      })
      .finally(() => {
        if (!active) return;
        setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  if (loading) {
    return (
      <section ref={sectionRef} className="py-20 px-6 relative">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
              Contest Leaderboards
            </h2>
            <p className="text-gray-400 text-lg">Loading leaderboards...</p>
          </div>
        </div>
      </section>
    );
  }

  if (error && leaderboards.length === 0) {
    return (
      <section ref={sectionRef} className="py-20 px-6 relative">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
              Contest Leaderboards
            </h2>
            <p className="text-red-400">{error}</p>
          </div>
        </div>
      </section>
    );
  }

  if (leaderboards.length === 0) {
    return null; // Don't show section if no leaderboards (IP restricted)
  }

  return (
    <section ref={sectionRef} className="py-20 px-6 relative" id="contests">
      <div className="max-w-7xl mx-auto">
        <div className={`text-center mb-12 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-purple-500/10 mb-6">
            <Trophy className="w-8 h-8 text-purple-400" />
          </div>
          <h2 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
            Contest Leaderboards & Rewards
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            Track contest participants, their points, and expected rewards.
          </p>
        </div>

        <div className="space-y-6">
          {leaderboards.map((lb) => (
            <div key={lb.contest_id} className={`bg-[#101018] border border-white/10 rounded-2xl p-6 transition-all duration-1000 delay-200 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              <div className="mb-4">
                <h3 className="text-xl font-semibold text-white">{lb.contest_title}</h3>
                <p className="text-sm text-gray-400">{lb.group_title}</p>
                <div className="mt-2 flex flex-wrap gap-4 text-sm text-gray-300">
                  <span>Daily Reward: {formatSol(lb.daily_reward_sol)}</span>
                  <span>Remaining Pool: {formatSol(lb.remaining_pool)}</span>
                  <span>Participants: {lb.total_participants}</span>
                  <span>Total Points: {lb.total_points}</span>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-white/10 text-left text-gray-400">
                      <th className="pb-2">Rank</th>
                      <th className="pb-2">User</th>
                      <th className="pb-2 text-right">Points</th>
                      <th className="pb-2 text-right">Share %</th>
                      <th className="pb-2 text-right">Expected Reward</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lb.leaderboard.map((entry) => (
                      <tr key={entry.user_id} className="border-b border-white/5 text-gray-300 hover:bg-white/5 transition">
                        <td className="py-2">#{entry.rank}</td>
                        <td className="py-2">
                          {entry.username}
                          {entry.wallet && (
                            <span className="text-xs text-gray-500 ml-2">
                              ({entry.wallet.slice(0, 4)}...{entry.wallet.slice(-4)})
                            </span>
                          )}
                        </td>
                        <td className="py-2 text-right">{entry.points}</td>
                        <td className="py-2 text-right">{entry.share_percent}%</td>
                        <td className="py-2 text-right text-purple-300 font-semibold">
                          {formatSol(entry.expected_reward_sol)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
